import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { FlagsContext, PortalConfigContext } from '../context';
import { usePlanStatus } from '../hooks/usePlanStatusHook';
import * as UserUtils from '../utils/UserUtils';
import * as Constants from '../constants';
import { GENERAL_OPERATIONS } from '../constants';

export interface RouteAccessChecks {
  allowCRMPages: boolean;
  allowHomePage: boolean;
  allowMessagesPage: boolean;
  allowFilesPage: boolean;
  allowPaymentsPage: boolean;
  allowKnowledgeBasePage: boolean;
  allowLandingPages: boolean;
  allowSubscriptionPage: boolean;
  allowProfilePage: boolean;
  allowSettingsCustomizationPage: boolean;
  allowSettingsBillingPage: boolean;
  allowSettingsModuleManagement: boolean;
  allowSettingsModuleMessaging: boolean;
  allowSettingsFiles: boolean;
  allowSettingsModulePayments: boolean;
  allowSettingsMainPage: boolean;
  allowSettingsMembersPage: boolean;
  allowSettingsApiPage: boolean;
  allowSettingsDomainPage: boolean;
  allowIntakeFormsPage: boolean;
  allowIntakeFormsResponsePage: boolean;
  allowClientDetailsPage: boolean;
  allowSettingsPlanPage: boolean;
  allowSettingsReferralPage: boolean;
  allowReferralPage: boolean;
  allowSettingsAppsPage: boolean;
  allowSettingsExtensionsPage: boolean;
  allowExtensionsPage: boolean;
  allowSignatureV2Page: boolean;
  allowNotificationSettings: boolean;
  allowClientImportPage: boolean;
}

export const useRouteAccess = (): RouteAccessChecks => {
  const portalConfig = useContext(PortalConfigContext);
  const planStatus = usePlanStatus();
  const isUnAuth = useSelector((state: RootState) => state.user.isUnAuth);
  const isClient = useSelector((state: RootState) => state.user.isClient);

  const {
    EnableUpdatedSignatureCreationFlow,
    EnableImprovedReferralsProgram,
  } = React.useContext(FlagsContext);
  const permissions = useSelector(
    (state: RootState) => state.user.data?.permissions,
  );
  const moduleSettings = useSelector(
    (state: RootState) => state.settings.moduleSettings,
  );
  const fileSettings = useSelector(
    (state: RootState) => state.settings.fileSettings,
  );
  const fileChannels = useSelector((state: RootState) => state.files.channels);

  /* Since portalConfig object is not updated in real time, we need
   * to get the enable module state from the moduleSettings.
   * This method gets default-module active state from module settings.
   * @param moduleId: default module id
   * @returns
   */
  const getDefaultModuleStateFromModuleSettings = (moduleId: string) => {
    if (!moduleSettings) return true;
    const moduleItem = moduleSettings.find(mod => moduleId === mod.id);
    if (moduleItem && moduleItem.disabled) return false;
    return true;
  };
  // files count for all channels
  const channelFilesCount = React.useMemo(
    () =>
      fileChannels
        .map(item => (item.fields.filesCount ? item.fields.filesCount : 0))
        .reduce((prev, curr) => prev + curr, 0),
    [fileChannels],
  );

  // for client don't show files page if visibility settings is off and no file is uploaded in a channel
  const hideFilesModule = () => {
    if (
      isClient &&
      !(fileSettings?.alwaysOnVisibility || channelFilesCount > 0)
    ) {
      return true;
    }

    return false;
  };

  return {
    allowProfilePage: !isUnAuth,
    allowCRMPages: UserUtils.hasPermission(Constants.PERM_MOD_CRM, permissions),
    allowHomePage: !isClient,
    allowMessagesPage:
      UserUtils.hasPermission(Constants.PERM_MOD_MESSAGES, permissions) &&
      getDefaultModuleStateFromModuleSettings('messages'),
    allowFilesPage:
      UserUtils.hasPermission(Constants.PERM_MOD_FILES, permissions) &&
      getDefaultModuleStateFromModuleSettings('files') &&
      !hideFilesModule(),
    allowPaymentsPage:
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
    allowKnowledgeBasePage:
      UserUtils.hasPermission(Constants.PERM_MOD_KNOWLEDGE_BASE, permissions) &&
      getDefaultModuleStateFromModuleSettings('knowledgeBase'),
    allowLandingPages:
      UserUtils.hasPermission(
        Constants.PERM_MOD_SETTINGS_LANDING_PAGE,
        permissions,
      ) && portalConfig.MarketingSite.Enabled,
    allowSubscriptionPage:
      !isUnAuth &&
      !isClient &&
      (UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions) ||
        getDefaultModuleStateFromModuleSettings('payments')),
    // settings page access configurations
    allowSettingsCustomizationPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_CUSTOMIZATION,
      permissions,
    ),
    // Settings/payments page is only visible for clients
    // when permission is allowed and the payments module is enabled in portalConfig,
    allowSettingsBillingPage:
      isClient &&
      UserUtils.hasPermission(
        Constants.PERM_MOD_SETTINGS_BILLING,
        permissions,
      ) &&
      getDefaultModuleStateFromModuleSettings('payments'),
    allowSettingsModuleManagement: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_MODULE_MANAGEMENT,
      permissions,
    ),
    allowSettingsFiles:
      UserUtils.hasPermission(Constants.PERM_MOD_SETTINGS_FILES, permissions) &&
      getDefaultModuleStateFromModuleSettings('files'),
    allowSettingsModulePayments:
      UserUtils.hasPermission(
        Constants.PERM_MOD_SETTINGS_MODULE_PAYMENTS,
        permissions,
      ) && getDefaultModuleStateFromModuleSettings('payments'),
    allowSettingsMainPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_MAIN_PG,
      permissions,
    ),
    allowSettingsModuleMessaging:
      UserUtils.hasPermission(
        Constants.PERM_MOD_SETTINGS_MESSAGING,
        permissions,
      ) && getDefaultModuleStateFromModuleSettings('messages'),
    allowSettingsMembersPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_MEMBERS,
      permissions,
    ),
    allowSettingsApiPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_API,
      permissions,
    ),
    allowSettingsAppsPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_APPS,
      permissions,
    ),
    allowSettingsExtensionsPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_EXTENSIONS,
      permissions,
    ),
    allowSettingsDomainPage: UserUtils.hasOperation(
      Constants.PERM_MOD_SETTINGS_DOMAINS,
      GENERAL_OPERATIONS.UPDATE,
      permissions,
    ),
    allowIntakeFormsPage:
      getDefaultModuleStateFromModuleSettings('forms') &&
      UserUtils.hasPermission(Constants.PERM_MOD_FORMS, permissions),
    allowIntakeFormsResponsePage:
      getDefaultModuleStateFromModuleSettings('forms') &&
      UserUtils.hasPermission(Constants.PERM_MOD_FORMS_RESPONSE, permissions),
    allowClientDetailsPage: !isClient,
    allowSettingsPlanPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_PLANS,
      permissions,
    ),
    allowSettingsReferralPage: !isClient && !planStatus.isTrialing,
    allowReferralPage:
      EnableImprovedReferralsProgram && !isClient && !planStatus.isTrialing,
    allowExtensionsPage: true,
    allowSignatureV2Page:
      UserUtils.hasPermission(Constants.PERM_MOD_FILES, permissions) &&
      getDefaultModuleStateFromModuleSettings('files') &&
      !hideFilesModule() &&
      EnableUpdatedSignatureCreationFlow, // Remove and replace with allowFilesPage when module is ready and flag can be removed.
    allowNotificationSettings: !isClient,
    allowClientImportPage: !isClient,
  };
};
